<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Progress } from 'dashboard/components-next/polaris/ui/progress';

const props = defineProps({
  dataImport: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();

const items = computed(() => {
  const importTypes = props.dataImport?.import_types || [];
  const groups = [];
  if (importTypes.includes('contacts')) {
    groups.push({ key: 'contacts', label: t('DATA_IMPORTS.TYPES.CONTACTS') });
  }
  if (importTypes.includes('conversations')) {
    groups.push(
      { key: 'conversations', label: t('DATA_IMPORTS.TYPES.CONVERSATIONS') },
      { key: 'messages', label: t('DATA_IMPORTS.TYPES.MESSAGES') }
    );
  }

  return groups.map(({ key, label }) => {
    const stats = props.dataImport?.stats?.[key] || {};
    const imported = Number(stats.imported || 0);
    const hasTotal = Object.prototype.hasOwnProperty.call(stats, 'total');
    const total = hasTotal ? Number(stats.total) : null;
    const percent =
      hasTotal && total > 0
        ? Math.min(100, Math.round((imported / total) * 100))
        : null;
    return {
      key,
      label,
      percent,
      importedLabel: imported.toLocaleString(),
      caption: hasTotal
        ? t('DATA_IMPORTS.DETAIL.PROGRESS_OF_TOTAL', {
            total: total.toLocaleString(),
          })
        : t('DATA_IMPORTS.DETAIL.PROGRESS_IMPORTED'),
    };
  });
});

// Fit the grid to the number of groups so no empty cells show.
const columnsClass = computed(() => {
  if (items.value.length >= 3) return 'sm:grid-cols-3';
  if (items.value.length === 2) return 'sm:grid-cols-2';
  return 'sm:grid-cols-1';
});
</script>

<template>
  <!-- Phase 9.9: a section card of the kit; the bar is the kit ui/progress. -->
  <section
    class="overflow-hidden rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card shadow"
  >
    <h2
      class="border-0 border-b border-solid border-polaris-border px-4 py-3 type-polaris-card-title"
    >
      {{ title }}
    </h2>
    <div
      class="grid grid-cols-1 gap-px bg-polaris-border"
      :class="columnsClass"
    >
      <div
        v-for="item in items"
        :key="item.key"
        class="flex flex-col gap-2 bg-polaris-card px-4 py-3"
      >
        <span class="type-polaris-caption text-polaris-muted-foreground">
          {{ item.label }}
        </span>
        <div class="flex items-end justify-between gap-2">
          <span class="tabular-nums type-polaris-section-title">
            {{ item.importedLabel }}
          </span>
          <span
            v-if="item.percent !== null"
            class="tabular-nums type-polaris-caption text-polaris-muted-foreground"
          >
            {{ `${item.percent}%` }}
          </span>
        </div>
        <Progress
          v-if="item.percent !== null"
          :model-value="item.percent"
          class="h-1.5"
        />
        <span class="type-polaris-caption text-polaris-muted-foreground">
          {{ item.caption }}
        </span>
      </div>
    </div>
  </section>
</template>
